import { useEffect, useState } from 'react';
import { X, UserMinus } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { setCorrection } from '../../services/correctionsService';
import { excludeUser } from '../../services/excludedUsersService';
import { StyledSelect, FIELD_CLASSES } from '../ui/StyledSelect';

export function OrgChartEditModal({ nodeId, data, photo, allEmployees, departments, onClose, onSaved, onRemoved }) {
  const { getApiToken } = useAuth();
  const [managerId, setManagerId]   = useState(data.managerId ?? '');
  const [department, setDepartment] = useState(data.department ?? '');
  const [jobTitle, setJobTitle]     = useState(data.jobTitle ?? '');
  const [topOfOrg, setTopOfOrg]     = useState(Boolean(data.topOfOrg));
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [error, setError]           = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(false);

  useEffect(() => {
    setManagerId(data.managerId ?? '');
    setDepartment(data.department ?? '');
    setJobTitle(data.jobTitle ?? '');
    setTopOfOrg(Boolean(data.topOfOrg));
    setError(null);
    setSaved(false);
    setConfirmRemove(false);
  }, [data]);

  async function handleSave() {
    setSaving(true);
    setError(null);
    try {
      const apiToken = await getApiToken();
      const patch = {};
      if (topOfOrg) {
        patch.topOfOrg = true;
        patch.managerId = null;
      } else {
        if (managerId !== (data.managerId ?? '')) patch.managerId = managerId || null;
        if ((data.topOfOrg ?? false) !== false) patch.topOfOrg = null;
      }
      if (department !== (data.department ?? '')) patch.department = department || null;
      if (jobTitle !== (data.jobTitle ?? '')) patch.jobTitle = jobTitle.trim() || null;
      if (Object.keys(patch).length > 0) await setCorrection(apiToken, nodeId, patch);
      setSaved(true);
      setTimeout(onSaved, 850);
    } catch (err) {
      setError(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  }

  async function handleReset() {
    setSaving(true);
    setError(null);
    try {
      const apiToken = await getApiToken();
      await setCorrection(apiToken, nodeId, { managerId: null, department: null, jobTitle: null, topOfOrg: null });
      setSaved(true);
      setTimeout(onSaved, 850);
    } catch (err) {
      setError(err.message || 'Failed to reset');
    } finally {
      setSaving(false);
    }
  }

  const ini = (name = '') => {
    const parts = name.trim().split(/\s+/);
    return parts.length === 1 ? parts[0][0]?.toUpperCase() ?? '?' : ((parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')).toUpperCase();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 dark:bg-black/60" onClick={onClose}>
      <div className="bg-white dark:bg-zinc-800 rounded-xl shadow-2xl p-5 w-80 border border-slate-200 dark:border-zinc-700" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 pb-3 mb-4 border-b border-slate-100 dark:border-zinc-700">
          {photo ? (
            <img src={photo} alt={data.displayName} className="w-9 h-9 rounded-full object-cover object-top flex-shrink-0" />
          ) : (
            <div className="w-9 h-9 rounded-full bg-teal-600 flex items-center justify-center text-white text-sm font-bold flex-shrink-0">{ini(data.displayName)}</div>
          )}
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900 dark:text-zinc-100 truncate">{data.displayName}</p>
            <p className="text-xs text-slate-500 dark:text-zinc-400 truncate">{data.jobTitle ?? '—'}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-zinc-300"><X size={16} /></button>
        </div>
        <div className="mb-4">
          <label className="block text-[10px] font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1.5">Reports to</label>
          <StyledSelect
            value={managerId}
            onChange={setManagerId}
            disabled={topOfOrg}
            placeholder="— No manager (root) —"
            options={[
              { value: '', label: '— No manager (root) —' },
              ...allEmployees.filter((e) => e.id !== nodeId).map((e) => ({ value: e.id, label: e.displayName })),
            ]}
          />
          <label className="mt-2 flex items-center gap-2 text-xs text-slate-600 dark:text-zinc-300">
            <input
              type="checkbox"
              checked={topOfOrg}
              onChange={(e) => {
                const next = e.target.checked;
                setTopOfOrg(next);
                if (next) setManagerId('');
              }}
              className="h-4 w-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500"
            />
            Mark as top of org
          </label>
          <p className="mt-1 text-[11px] text-slate-400 dark:text-zinc-500">
            Use this for the interim CEO or other temporary root. The chart will build the rest of the leadership tree under this person.
          </p>
        </div>
        <div className="mb-5">
          <label className="block text-[10px] font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1.5">Title</label>
          <input
            value={jobTitle}
            onChange={(e) => setJobTitle(e.target.value)}
            className={FIELD_CLASSES}
            placeholder="e.g. Director of Operations"
          />
        </div>
        <div className="mb-5">
          <label className="block text-[10px] font-semibold text-slate-500 dark:text-zinc-400 uppercase tracking-wide mb-1.5">Department</label>
          <StyledSelect
            value={department}
            onChange={setDepartment}
            placeholder="— No department —"
            options={[
              { value: '', label: '— No department —' },
              // Keep a corrected value visible even if it's not in the live list
              ...(department && !departments.includes(department) ? [{ value: department, label: department }] : []),
              ...departments.map((d) => ({ value: d, label: d })),
            ]}
          />
        </div>
        {error && <p className="text-xs text-rose-600 mb-3">{error}</p>}
        {saved && !error && <p className="text-xs font-medium text-teal-600 mb-3">✓ Saved — updating chart…</p>}
        <div className="flex gap-2">
          <button onClick={handleSave} disabled={saving || saved} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold py-2 rounded-lg transition-colors disabled:opacity-50">{saved ? 'Saved ✓' : saving ? 'Saving…' : 'Save'}</button>
          <button onClick={onClose} className="flex-1 bg-slate-100 dark:bg-zinc-700 hover:bg-slate-200 dark:hover:bg-zinc-600 text-slate-700 dark:text-zinc-300 text-sm py-2 rounded-lg transition-colors">Cancel</button>
          <button onClick={handleReset} disabled={saving} title="Reset to Graph values" className="px-3 bg-white dark:bg-zinc-900 border border-rose-200 dark:border-rose-800 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/30 text-sm py-2 rounded-lg transition-colors disabled:opacity-50">↺</button>
        </div>

        {/* Remove from directory — destructive, so it asks for confirmation
            instead of firing on first click. */}
        {onRemoved && (
          <div className="mt-4 pt-3 border-t border-slate-100 dark:border-zinc-700">
            {confirmRemove ? (
              <div className="flex items-center gap-2">
                <p className="flex-1 text-xs text-slate-500 dark:text-zinc-400">
                  Hide {data.displayName?.split(' ')[0]} from the directory?
                </p>
                <button
                  onClick={() => { excludeUser(nodeId, data.displayName); onRemoved(); }}
                  className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold transition-colors"
                >
                  Remove
                </button>
                <button
                  onClick={() => setConfirmRemove(false)}
                  className="px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-zinc-700 hover:bg-slate-200 dark:hover:bg-zinc-600 text-slate-600 dark:text-zinc-300 text-xs font-medium transition-colors"
                >
                  Keep
                </button>
              </div>
            ) : (
              <button
                onClick={() => setConfirmRemove(true)}
                className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 text-xs font-medium transition-colors"
              >
                <UserMinus size={13} />
                Remove from directory
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
